import { css } from '@emotion/react';
import {
  type FC, useState,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { OverlayWrapperComponent } from '~/_shared/components/overlay/overlayWrapper.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { type Pagination } from '~/_shared/types/pagination/pagination';
import { MobileEmptyMapListingComponent } from '~/map/listing/mobileEmptyMapListingState.component';
import { type ShareMapProps } from '~/map/share/shareMap.container';
import { type MapListingItem } from './item/mapListingItem';
import { MobileTableDataComponent } from './mobileTableData/mobileTableData.component';

const scrollBarStyle = css({
  minHeight: 'unset',
});

const containerStyle = css({
  '&:before': {
    content: '""',
    clear: 'both',
    display: 'table',
  },
});

const originalTableWrapperStyle = (marginTop: number) => (theme: Theme) => css({
  marginTop,
  overflow: 'hidden',
  boxShadow: `0 2px 5px ${theme.shadowColors.secondary}`,
});

const emptyStyle = css({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
});

const alertWrapperStyle = css({
  margin: 10,
});

const spinnerStyle = css({
  position: 'fixed',
});

type MobileMapListingProps = Readonly<{
  errorMessage: string | null;
  isMapListingDataLoading: boolean;
  pagination: Pagination | null;
  processedItems: ReadonlyArray<MapListingItem>;
  selectedMapIds: ReadonlyArray<number>;
  selectedSnapshotIds: ReadonlySet<number>;
  toggledMapIds: ReadonlyArray<number>;

  onMapShareSettingsClick: (props: ShareMapProps) => void;
  onPageChange: (page: number) => void;
  toggleSubMapVisibility: (mapId: number) => void;
}>;

export const MobileMapListingComponent: FC<MobileMapListingProps> = (props) => {
  const [sortAndFiltersHeight] = useState(0);
  const hasContent = props.errorMessage || props.isMapListingDataLoading || props.processedItems.length > 0;

  return (
    hasContent ? (
      <ScrollBarComponent css={scrollBarStyle} type={ScrollbarType.Vertical}>
        <div css={containerStyle}>
          <div css={originalTableWrapperStyle(sortAndFiltersHeight)}>
            <OverlayWrapperComponent>
              <MobileTableDataComponent
                data={props.processedItems}
                onShowSubMapsToggle={props.toggleSubMapVisibility}
                toggledMapIds={props.toggledMapIds}
                pagination={props.pagination}
                onPageChange={props.onPageChange}
                isMapListingDataLoading={props.isMapListingDataLoading}
                onMapShareSettingsClick={props.onMapShareSettingsClick}
              />

              {props.errorMessage && (
                <div css={alertWrapperStyle}>
                  <AlertComponent type="danger">{props.errorMessage}</AlertComponent>
                </div>
              )}

              {props.isMapListingDataLoading &&
                <OverlayLoaderComponent spinnerStyle={props.processedItems.length >= 10 ? spinnerStyle : undefined} />
              }
            </OverlayWrapperComponent>
          </div>
        </div>
      </ScrollBarComponent>
    ) : (
      <div css={emptyStyle}>
        <MobileEmptyMapListingComponent />
      </div>
    )
  );
};
